import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import CloseIcon from '@mui/icons-material/Close';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@mui/material";
import classNames from 'classnames';
import React, { isValidElement, useCallback, useState } from "react";
import styles from "../styles/components/color-picker.module.scss";


// Example preset color palette
const PRESET_COLORS = [
  "#000000",
  "#FFFFFF",
  "#FE2712",
  "#FEFE33",
  "#0247FE",
  "#FB9902",
  "#66B032",
  "#8601AF",
];

interface ColorSwatchProps {
  color?: string;
  size?: 'small' | 'medium' | 'large';
  onChangeColour?: (color: string) => void;
  hidden?: boolean;
}

export function ColorSwatch({
  color,
  size = 'small',
  onChangeColour,
  hidden = false,
}: ColorSwatchProps) {
  if (onChangeColour) {
    return (
      <ColourPickerButton onChangeColour={onChangeColour}>
        <div className={classNames(styles.tabColor, {
          [styles[size]]: true,
        })} style={{backgroundColor: color || '#ffffff'}} />
      </ColourPickerButton>
    )
  }

  return (
    <div 
      className={classNames(styles.tabColor, {
        [styles[size]]: true,
        [styles.hidden]: hidden,
      })} 
      style={{backgroundColor: color || '#ffffff'}}
    >
      {hidden && <Tooltip
            title={'Not shown on the map'} 
            placement="bottom"
             
            arrow
          >
            <span><VisibilityOffOutlinedIcon /></span></Tooltip>}
    </div>
  )
}

interface ColourPickerButtonProps {
  color?: string;
  onTogglePicker?: (show: boolean) => void;
  onChangeColour: (color: string) => void;
  children?: React.ReactNode;
}

export function ColourPickerButton({
  color,
  onTogglePicker,
  onChangeColour,
  children,
}: ColourPickerButtonProps) {
  const [showPicker, setShowPicker] = useState<boolean>(false);
  
  const handleClick = useCallback(() => {
    setShowPicker(true)
    if (onTogglePicker) {
      onTogglePicker(true)
    }
  },[])

  return (
    <>
    <Tooltip
      title={'Line colour'}
      placement="bottom"
      arrow
    >
      <span>
        {children && isValidElement(children) ? 
            // @ts-ignore
            <>{React.cloneElement( children, { onClick: handleClick } ) }</>
          : 
          <IconButton
            size='small'
            onClick={() => {
              handleClick()
            }
          }>
            <ColorLensIcon />
          </IconButton>
        }
        
      </span>
    </Tooltip>
    {showPicker && 
      <ColorPickerWithPresets
        closeOnSelect
        initialColour={color || '#ffffff'}
        onChangeColour={async (newColor: string) => {
          onChangeColour(newColor)
        }}
        onClose={() => {
          setShowPicker(false)
          if (onTogglePicker) {
            onTogglePicker(false)
          }
        }}
      />}
    </>
  )
}

interface ColorPickerProps {
  presets?: string[];
  onClose: () => void;
  initialColour?: string;
  onChangeColour?: (color: string) => void;
  closeOnSelect?: boolean;
}

export default function ColorPickerWithPresets({ 
  presets = PRESET_COLORS, 
  onClose,
  initialColour = presets[0],
  onChangeColour,
  closeOnSelect = false,
}: ColorPickerProps) {
  const [selected, setSelected] = useState(initialColour);

  // useEffect(() => {
  //   if (onChangeColour) {
  //     onChangeColour(selected)
  //   }
  // }, [selected]);

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 400 }} className={styles.pickerWrapper}>
      <IconButton
        className={styles.closeButton}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      
      <Typography variant="h6" gutterBottom>
        Shape colour
      </Typography>

      {/* Preview */}
      {!closeOnSelect && <Box
        sx={{
          width: "100%",
          height: 80,
          backgroundColor: selected,
          borderRadius: 1,
          mb: 3,
          border: "1px solid #ccc",
        }}
      />}

      {/* Swatches */}
      <Grid container spacing={1}>
        {presets.map((color) => (
          <Grid key={color}>
            <Box
              onClick={() => {
                setSelected(color)
                if (onChangeColour) {
                  onChangeColour(color)
                }
                if (closeOnSelect) {
                    onClose()
                  }          
                }
              }
              sx={{
                width: 32,
                height: 32,
                borderRadius: "4px",
                backgroundColor: color,
                cursor: "pointer",
                border:
                  selected === color ? "2px solid black" : "1px solid #ccc",
                transition: "0.15s",
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}
